import {z} from "zod";

/**
 * Where a customer came from. Each AI assistant stays apart from the others and
 * from search, and paid traffic stays apart from organic.
 */
export const acquisitionChannels = ["claude", "chatgpt", "ai_other", "paid_search", "organic_search", "paid_social", "social", "apple_ads", "email", "paid_other", "referral", "direct"] as const;
export type AcquisitionChannel = (typeof acquisitionChannels)[number];

const tag = z.string().max(100).optional();
/**
 * Landing query parameters a touch keeps: campaign tags and ad click IDs. Google
 * Ads adds `gad_source`/`gad_campaignid` even where Safari strips `gclid`.
 */
const paramsSchema = z.object({
  utm_source: tag,
  utm_medium: tag,
  utm_campaign: tag,
  utm_term: tag,
  utm_content: tag,
  utm_id: tag,
  gclid: tag,
  gbraid: tag,
  wbraid: tag,
  gad_source: tag,
  gad_campaignid: tag,
  msclkid: tag,
  fbclid: tag,
  ttclid: tag
});
export const attributionParams = paramsSchema.keyof().options;
export type AttributionParams = z.infer<typeof paramsSchema>;

/** One visit as the browser saw it. Lengths keep both touches inside one cookie. */
export const attributionTouchSchema = z.object({
  at: z.string().datetime(),
  /** Landing path, without the query. */
  landing: z.string().max(200),
  /** Referring host, empty for direct visits and first-party, sign-in or payment pages. */
  referrer: z.string().max(253),
  params: paramsSchema
});
export type AttributionTouch = z.infer<typeof attributionTouchSchema>;

/** The first visit and the latest one that came from somewhere (campaign tags or another site). */
export const attributionTouchesSchema = z.object({first: attributionTouchSchema.nullable(), last: attributionTouchSchema.nullable()});
export type AttributionTouches = z.infer<typeof attributionTouchesSchema>;

/** First-party cookie on the parent domain, so the website, the web app and the API all see it. */
export const attributionCookie = {name: "sprout_attribution", maxAgeSeconds: 60 * 60 * 24 * 180} as const;

/** Sprout itself, and the sign-in and payment pages it sends people through, are never where someone came from. */
const internalReferrerHost = /(^|\.)(z4k\.app|authkit\.app|workos\.com|accounts\.google\.com|appleid\.apple\.com|stripe\.com|localhost)$|^127\.0\.0\.1$/;

export function isInternalReferrer(host: string): boolean {
  return internalReferrerHost.test(host.toLowerCase());
}

/** A touch's channel, the network or site behind it, and its campaign. */
export interface TouchChannel {
  channel: AcquisitionChannel;
  source: string;
  campaign: string;
}

const paidMediums = new Set(["cpc", "ppc", "paid", "paidsearch", "paid_search", "paid-search", "paidsocial", "paid_social", "paid-social", "display", "cpm", "cpv", "ads", "ad", "sponsored"]);

// `[source name, utm_source or referring host]`. ChatGPT citation links carry `utm_source=chatgpt.com` and no referrer.
const assistants: ReadonlyArray<readonly [string, RegExp]> = [
  ["claude", /(^|\.)(claude\.ai|claude\.com|anthropic\.com)$|^(claude|anthropic)$/],
  ["chatgpt", /(^|\.)(chatgpt\.com|openai\.com|oaiusercontent\.com)$|^(chatgpt|openai)$/],
  ["perplexity", /(^|\.)perplexity\.ai$|^perplexity$/],
  ["gemini", /(^|\.)gemini\.google(\.com)?$|^gemini$/],
  ["copilot", /(^|\.)copilot\.(microsoft\.com|com)$|^copilot$/],
  ["meta_ai", /(^|\.)meta\.ai$/],
  ["grok", /(^|\.)grok\.com$|^grok$/],
  ["deepseek", /(^|\.)deepseek\.com$|^deepseek$/],
  ["mistral", /(^|\.)chat\.mistral\.ai$/]
];
const searchEngines: ReadonlyArray<readonly [string, RegExp]> = [
  ["google", /(^|\.)google(\.[a-z]{2,3}){1,2}$|^google$/],
  ["bing", /(^|\.)bing\.com$|^(bing|microsoft)$/],
  ["duckduckgo", /(^|\.)duckduckgo\.com$|^duckduckgo$/],
  ["yahoo", /(^|\.)search\.yahoo\.com$|^yahoo$/],
  ["ecosia", /(^|\.)ecosia\.org$|^ecosia$/],
  ["brave", /(^|\.)search\.brave\.com$|^brave$/],
  ["yandex", /(^|\.)yandex\.[a-z]+$|^yandex$/],
  ["baidu", /(^|\.)baidu\.com$|^baidu$/]
];
const socialNetworks: ReadonlyArray<readonly [string, RegExp]> = [
  ["facebook", /(^|\.)(facebook\.com|fb\.com|fb\.me)$|^(facebook|fb|meta)$/],
  ["instagram", /(^|\.)instagram\.com$|^(instagram|ig)$/],
  ["tiktok", /(^|\.)tiktok\.com$|^tiktok$/],
  ["reddit", /(^|\.)reddit\.com$|^reddit$/],
  ["x", /(^|\.)(x\.com|twitter\.com|t\.co)$|^(x|twitter)$/],
  ["youtube", /(^|\.)(youtube\.com|youtu\.be)$|^youtube$/],
  ["pinterest", /(^|\.)pinterest\.[a-z.]+$|^pinterest$/],
  ["linkedin", /(^|\.)(linkedin\.com|lnkd\.in)$|^linkedin$/],
  ["threads", /(^|\.)threads\.(net|com)$|^threads$/],
  ["snapchat", /(^|\.)snapchat\.com$|^snapchat$/]
];
const appleAds = /^(apple_?ads|apple_?search_?ads|asa)$/;

function match(list: ReadonlyArray<readonly [string, RegExp]>, value: string): string | null {
  return list.find(([, pattern]) => pattern.test(value))?.[0] ?? null;
}

/** Each assistant keeps its own channel; the rest share one. */
function assistantChannel(name: string): AcquisitionChannel {
  return name === "claude" || name === "chatgpt" ? name : "ai_other";
}

/** Classifies one browser touch from its raw landing tags and referrer, so stored touches can be reclassified later. */
export function touchChannel(touch: AttributionTouch): TouchChannel {
  const {params} = touch;
  const source = params.utm_source?.toLowerCase() ?? "";
  const paid = paidMediums.has(params.utm_medium?.toLowerCase() ?? "");
  const host = isInternalReferrer(touch.referrer) ? "" : touch.referrer.toLowerCase();
  // An auto-tagged Google Ads click names its campaign only by ID.
  const campaign = params.utm_campaign ?? params.gad_campaignid ?? params.utm_id ?? "";
  const result = (channel: AcquisitionChannel, name: string): TouchChannel => ({channel, source: name, campaign});

  if (params.gclid || params.gbraid || params.wbraid || params.gad_source || params.gad_campaignid) return result("paid_search", "google");
  if (params.msclkid) return result("paid_search", "bing");
  if (params.ttclid) return result("paid_social", "tiktok");

  if (source) {
    if (appleAds.test(source) || (paid && source === "apple")) return result("apple_ads", "apple");
    const assistant = match(assistants, source);
    const search = match(searchEngines, source);
    const social = match(socialNetworks, source);
    if (paid) {
      // ChatGPT's ads answer a typed question, like a search ad.
      if (search || assistant) return result("paid_search", search ?? assistant ?? source);
      if (social) return result("paid_social", social);
      return result("paid_other", source);
    }
    if (params.utm_medium?.toLowerCase() === "email" || source === "newsletter" || source === "lifecycle") return result("email", source);
    if (assistant) return result(assistantChannel(assistant), assistant);
    if (search) return result("organic_search", search);
    if (social) return result("social", social);
    return result("referral", source);
  }

  // Facebook tags organic shares with `fbclid` too; its ads carry a paid `utm_medium`.
  if (params.fbclid) return result("social", "facebook");
  if (host) {
    const assistant = match(assistants, host);
    if (assistant) return result(assistantChannel(assistant), assistant);
    const search = match(searchEngines, host);
    if (search) return result("organic_search", search);
    const social = match(socialNetworks, host);
    if (social) return result("social", social);
    return result("referral", host.replace(/^www\./, ""));
  }
  return result("direct", "direct");
}

/** Ad click IDs, which let each ad platform match a conversion to the click behind it. */
export const adClickParams = ["gclid", "gbraid", "wbraid", "msclkid", "fbclid", "ttclid"] as const;
export type AdClickIds = Partial<Record<(typeof adClickParams)[number], string>>;

/** The click IDs of the latest touch that carried one: the ad click closest to the conversion. */
export function adClickIds(touches: AttributionTouches): AdClickIds {
  for (const touch of [touches.last, touches.first]) {
    const ids: AdClickIds = {};
    for (const key of adClickParams) {
      const value = touch?.params[key];
      if (value) ids[key] = value;
    }
    if (Object.keys(ids).length > 0) return ids;
  }
  return {};
}

/** App Store Connect counts a `ct` campaign token of up to 30 characters. */
const campaignTokenLength = 30;

/** The campaign token naming where a visit came from, as `channel.source.campaign`. */
export function appStoreCampaignToken(touch: AttributionTouch | null): string {
  const {channel, source, campaign} = touch ? touchChannel(touch) : {channel: "direct", source: "direct", campaign: ""};
  const parts = [channel, source === channel ? "" : source, campaign].filter(Boolean);
  return parts
    .join(".")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "_")
    .slice(0, campaignTokenLength);
}

/**
 * An App Store link that App Analytics credits to a campaign. Apple ignores `ct`
 * without the provider token `pt`, so the link stays plain until one is configured.
 */
export function appStoreCampaignUrl(url: string, providerId: string, campaignToken: string): string {
  if (!providerId) return url;
  const link = new URL(url);
  link.searchParams.set("pt", providerId);
  link.searchParams.set("ct", campaignToken.slice(0, campaignTokenLength));
  link.searchParams.set("mt", "8");
  return link.toString();
}
